// Shot list workspace: scene list beside the selected scene's shot table (cards on phones).
import { Fragment, useState } from 'react';
import { randomId } from '../../shared/ids';
import { capturedCount, formatTime, outcomeSummary, sceneNumber, sceneStarts, type Episode, type Scene, type Shot } from '../../shared/model';
import { SHOT_ANGLES, SHOT_MOVEMENTS, SHOT_SIZES, term } from '../../shared/vocab';
import { assetUrl } from '../api';
import type { EpisodeSync } from '../sync';
import { Icon, Ring, sceneColour } from '../components/Icon';
import { ShotSizeIcon } from '../components/ShotIcons';
import { useMediaQuery } from '../components/ui';
import { sceneThumbnail } from './Cards';
import { filmSlot } from '../../shared/checklist';
import { JournalRefs, OutcomeControl, SkipReason } from '../components/Outcome';
import { ShotRow } from './Shots';
import { href, navigate } from '../router';

type Filter = 'all' | 'open' | 'filmed' | 'skipped' | 'essential' | 'optional';
const shotRef = (sceneIndex: number, shotIndex: number) => `${sceneIndex + 1}${shotIndex < 26 ? String.fromCharCode(65 + shotIndex) : `.${shotIndex + 1}`}`;

interface Props {
  episode: Episode;
  engine: EpisodeSync;
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  onEditSketch: (sceneId: string) => void;
  /** Gear filter is kept by the episode page so the Kit tab can open "shots using X". */
  gearFilter: string;
  setGearFilter: (gearId: string) => void;
}

export function ShotsTab({ episode, engine, selectedId, setSelectedId, onEditSketch, gearFilter, setGearFilter }: Props) {
  const mobile = useMediaQuery('(max-width: 760px)');
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const scenes = episode.scenes;
  const index = Math.max(0, scenes.findIndex((s) => s.id === selectedId));
  const scene: Scene | undefined = scenes[index];
  const starts = sceneStarts(episode);
  const gear = episode.gear ?? [];
  const gearName = new Map(gear.map((g) => [g.id, g.name]));
  const gearMatches = (s: Scene) => (gearFilter ? s.shots.filter((sh) => (sh.gear ?? []).includes(gearFilter)).length : null);

  if (!scene) {
    return (
      <section className="overview-empty">
        <h2>No scenes yet</h2>
        <p className="muted">Shots belong to scenes. Add a scene in the planner first.</p>
        <a className="btn primary" href={href.episode(episode.id, 'plan')}>
          Open the planner
        </a>
      </section>
    );
  }

  const sceneCounts = capturedCount(scene);
  const counts: Record<Filter, number> = {
    all: scene.shots.length,
    open: sceneCounts.open,
    filmed: sceneCounts.filmed,
    skipped: sceneCounts.skipped,
    essential: scene.shots.filter((s) => s.priority === 'essential').length,
    optional: scene.shots.filter((s) => s.priority === 'optional').length,
  };
  const matchesFilter = (shot: Shot) =>
    filter === 'all' ? true : filter === 'essential' || filter === 'optional' ? shot.priority === filter : shot.outcome === filter;
  const visible = scene.shots
    .map((shot, j) => ({ shot, j }))
    .filter(({ shot }) => matchesFilter(shot))
    .filter(({ shot }) => !gearFilter || (shot.gear ?? []).includes(gearFilter));

  const update = (shot: Shot, fields: Partial<Shot>) => engine.queue({ op: 'updateShot', sceneId: scene.id, shotId: shot.id, fields: fields as never });
  const addShot = () => {
    const description = draft.trim();
    if (!description) return;
    const id = randomId('sh');
    engine.queue({ op: 'addShot', sceneId: scene.id, id, shot: { description, priority: 'essential' } });
    setDraft('');
    setEditing(id);
  };
  const specChips = (shot: Shot) =>
    [term(SHOT_SIZES, shot.size)?.abbr, term(SHOT_ANGLES, shot.angle)?.abbr, term(SHOT_MOVEMENTS, shot.movement)?.abbr]
      .filter(Boolean)
      .map((t) => (
        <span key={t} className="spec-chip">
          {t}
        </span>
      ));
  const gearChips = (shot: Shot) =>
    (shot.gear ?? []).map((id) => (
      <span key={id} className="gear-tag">
        {gearName.get(id)}
      </span>
    ));
  const editor = (shot: Shot, j: number) => (
    <ol className="shot-list shot-editor">
      <ShotRow shot={shot} index={j} count={scene.shots.length} scene={scene} engine={engine} gear={gear} />
    </ol>
  );
  const thumb = sceneThumbnail(scene);

  const filters = (
    <div className="filter-chips" role="toolbar" aria-label="Filter shots">
      {(
        [
          ['all', 'All'],
          ['open', 'Open'],
          ['filmed', 'Filmed'],
          ['skipped', 'Skipped'],
          ['essential', 'Essential'],
          ['optional', 'Optional'],
        ] as [Filter, string][]
      ).map(([k, label]) => (
        <button key={k} type="button" className={`filter-chip ${filter === k ? 'on' : ''}`} aria-pressed={filter === k} onClick={() => setFilter(k)}>
          {label} <span>{counts[k]}</span>
        </button>
      ))}
      {gear.length > 0 && (
        <select className={`filter-select ${gearFilter ? 'on' : ''}`} aria-label="Filter by gear" value={gearFilter} onChange={(e) => setGearFilter(e.target.value)}>
          <option value="">All gear</option>
          {gear.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      )}
      {/* Explain a short list when filters hide shots, e.g. "All 5" showing one row. */}
      {visible.length !== scene.shots.length && (
        <span className="filter-result" role="status">
          Showing {visible.length} of {scene.shots.length} shot{scene.shots.length === 1 ? '' : 's'}
          {gearFilter && <> · with {gearName.get(gearFilter)}</>}
          <button
            type="button"
            className="link-btn"
            onClick={() => {
              setFilter('all');
              setGearFilter('');
            }}
          >
            Show all
          </button>
        </span>
      )}
    </div>
  );

  const addForm = (
    <form
      className="add-shot"
      onSubmit={(e) => {
        e.preventDefault();
        addShot();
      }}
    >
      <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={`Add a shot to ${sceneNumber(index)}…`} aria-label="New shot description" />
      <button className="btn primary" disabled={!draft.trim()}>
        <Icon name="plus" size={16} /> Shot
      </button>
    </form>
  );

  if (mobile) {
    return (
      <div className="shots-mobile">
        <div className="scene-strip" role="tablist" aria-label="Scenes">
          {scenes.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`${i === index ? 'on' : ''} ${gearMatches(s) === 0 ? 'no-match' : ''}`}
              onClick={() => (setSelectedId(s.id), setEditing(null))}
            >
              <span className="scene-dot" style={{ background: sceneColour(i) }} />
              {sceneNumber(i)}
              {!!gearMatches(s) && <span className="match-count">{gearMatches(s)}</span>}
            </button>
          ))}
        </div>
        <div className="shots-mobile-head">
          <div className="eyebrow">
            {sceneNumber(index)} · {formatTime(scene.durationSeconds)} · {scene.shots.length} shots
          </div>
          <h2>{scene.title.replace(/^\d+\s*·\s*/, '')}</h2>
          {filters}
        </div>
        <ul className="shot-cards">
          {visible.map(({ shot, j }) => (
            <li key={shot.id} className={`shot-card is-${shot.outcome}`}>
              <button type="button" className="shot-card-body" onClick={() => setEditing(editing === shot.id ? null : shot.id)} aria-expanded={editing === shot.id}>
                <b>
                  {shotRef(index, j)}
                  {shot.framing ? ` · ${shot.framing}` : ''}
                </b>
                <span className="shot-card-desc">{shot.description}</span>
                <span className="chip-row">
                  {specChips(shot)}
                  {shot.priority === 'essential' && <span className="spec-chip essential">★ Essential</span>}
                </span>
                {(shot.gear ?? []).length > 0 && <span className="chip-row">{gearChips(shot)}</span>}
              </button>
              <div className="shot-card-outcome">
                <OutcomeControl shot={shot} size="lg" labels onChange={(f) => update(shot, f)} />
                <SkipReason shot={shot} onChange={(skipReason) => update(shot, { skipReason })} />
                <JournalRefs refs={shot.journalRefs} />
              </div>
              {editing === shot.id && <div className="shot-card-editor">{editor(shot, j)}</div>}
            </li>
          ))}
          {!visible.length && <li className="muted small">No shots match this filter.</li>}
        </ul>
        {addForm}
      </div>
    );
  }

  return (
    <div className="shots-tab">
      <aside className="scene-pane" aria-label="Scenes">
        <div className="scene-pane-head">
          <h3>Scenes</h3>
          <span className="muted">{scenes.length}</span>
        </div>
        <ol>
          {scenes.map((s, i) => {
            const c = capturedCount(s);
            const matches = gearMatches(s);
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className={`scene-pane-item ${i === index ? 'on' : ''} ${matches === 0 ? 'no-match' : ''}`}
                  aria-current={i === index}
                  onClick={() => (setSelectedId(s.id), setEditing(null))}
                >
                  <span className="scene-bar" style={{ background: sceneColour(i) }} />
                  <span className="scene-pane-text">
                    <b>
                      {sceneNumber(i)} {s.title.replace(/^\d+\s*·\s*/, '')}
                    </b>
                    <span>
                      {formatTime(s.durationSeconds)} · {s.shots.length} shot{s.shots.length === 1 ? '' : 's'}
                      {matches ? <b className="match-count">{matches} with {gearName.get(gearFilter)}</b> : null}
                    </span>
                  </span>
                  <Ring value={c.total ? (c.filmed + c.skipped) / c.total : 0} size={22} stroke={2.6} label={outcomeSummary(c)} />
                </button>
              </li>
            );
          })}
        </ol>
      </aside>
      <section className="shots-main">
        <header className="scene-head">
          <button type="button" className="scene-head-img" onClick={() => onEditSketch(scene.id)} aria-label={scene.storyboard ? 'Edit storyboard' : 'Draw storyboard'}>
            {thumb ? <img src={assetUrl(thumb, 480)} alt={scene.storyboard?.description || ''} /> : <span>+ Sketch</span>}
          </button>
          <div className="scene-head-text">
            <div className="eyebrow">
              Scene {sceneNumber(index)} · {formatTime(starts[index])}–{formatTime(starts[index] + scene.durationSeconds)}
            </div>
            <h2>{scene.title.replace(/^\d+\s*·\s*/, '')}</h2>
            <div className="meta-chips">
              <span>
                <Icon name="clock" size={14} /> {formatTime(scene.durationSeconds)}
              </span>
              {filmSlot(scene) && <span>🎬 {filmSlot(scene)}</span>}
              <span className={`pill ${scene.status === 'ready' ? 'green' : 'amber'}`}>
                <i />
                {scene.status === 'ready' ? 'Ready to film' : 'Draft'}
              </span>
              <span className="meta-actions">
                <button type="button" className="btn small" onClick={() => onEditSketch(scene.id)}>
                  <Icon name="pencil" size={15} /> {scene.storyboard ? 'Edit sketch' : 'Draw sketch'}
                </button>
                <button type="button" className="btn small" onClick={() => navigate(href.episode(episode.id, 'plan'))}>
                  <Icon name="timeline" size={15} /> Open in planner
                </button>
              </span>
            </div>
            {/* Location/route text can be long: give it its own wrapping line instead of a clipped chip. */}
            {scene.location && (
              <p className="scene-location">
                <Icon name="pin" size={15} />
                <span>{scene.location}</span>
              </p>
            )}
            {scene.purpose && (
              <p className="scene-purpose">
                <b>Why it belongs:</b> {scene.purpose}
              </p>
            )}
          </div>
        </header>
        {filters}
        <table className="shot-table">
          <thead>
            <tr>
              <th className="col-check" aria-label="Captured" />
              <th className="col-ref">Shot</th>
              <th>Description</th>
              <th className="col-setup">Setup &amp; gear</th>
              <th className="col-pri">Priority</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(({ shot, j }) => (
              <Fragment key={shot.id}>
                <tr
                  className={`is-${shot.outcome} ${editing === shot.id ? 'editing' : ''}`}
                  tabIndex={0}
                  aria-expanded={editing === shot.id}
                  aria-label={`Shot ${shotRef(index, j)}: ${shot.description}`}
                  onClick={() => setEditing(editing === shot.id ? null : shot.id)}
                  onKeyDown={(e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setEditing(editing === shot.id ? null : shot.id);
                    }
                  }}
                >
                  <td className="col-check" onClick={(e) => e.stopPropagation()}>
                    <OutcomeControl shot={shot} onChange={(f) => update(shot, f)} />
                  </td>
                  <td className="col-ref">{shotRef(index, j)}</td>
                  <td className="col-desc">
                    {shot.framing && <b className="shot-framing-text">{shot.framing}</b>}
                    <span className="shot-desc-text">{shot.description}</span>
                    {/* Camera notes stay folded until the shot is opened. */}
                    {shot.cameraNotes && editing !== shot.id && (
                      <span className="shot-notes-hint" title={shot.cameraNotes}>
                        <Icon name="notes" size={13} /> Camera notes
                      </span>
                    )}
                    <SkipReason shot={shot} onChange={(skipReason) => update(shot, { skipReason })} />
                    <JournalRefs refs={shot.journalRefs} />
                  </td>
                  <td className="col-setup">
                    <span className="spec-cell">
                      <ShotSizeIcon size={shot.size} />
                      <span className="chip-row">{specChips(shot)}</span>
                    </span>
                    {(shot.gear ?? []).length > 0 && <span className="chip-row gear-row">{gearChips(shot)}</span>}
                  </td>
                  <td className="col-pri" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className={`pill ${shot.priority === 'essential' ? 'green' : 'grey'}`} onClick={() => update(shot, { priority: shot.priority === 'essential' ? 'optional' : 'essential' })} aria-label={`Priority ${shot.priority}; click to change`}>
                      <i />
                      {shot.priority === 'essential' ? 'Essential' : 'Optional'}
                    </button>
                  </td>
                </tr>
                {editing === shot.id && (
                  <tr className="editor-row">
                    <td colSpan={5}>{editor(shot, j)}</td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!visible.length && (
              <tr>
                <td colSpan={5} className="muted small">
                  No shots match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {addForm}
        <p className="muted small capture-note">Click a row to edit it and see its camera notes. ○ open · ✓ filmed (reported, not reviewed) · ✕ skipped on purpose, so there is no footage.</p>
      </section>
    </div>
  );
}
